import React, {useEffect, useState} from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import {ArrowDownward, ArrowUpward} from '@mui/icons-material';

const LINK_TYPES = [
    {key: 'review1', label: 'Ссылка на отзыв №1'},
    {key: 'review2', label: 'Ссылка на отзыв №2'},
    {key: 'review3', label: 'Ссылка на отзыв №3'},
    {key: 'review4', label: 'Ссылка на отзыв №4'},
    {key: 'review5', label: 'Ссылка на отзыв №5'},
];

const ReviewLinksModal = ({open, onClose, initialConfig, onSave}) => {
    const [links, setLinks] = useState({});
    const [order, setOrder] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (open && initialConfig) {
            setLinks(initialConfig.links || {});
            setOrder(initialConfig.order || []);
        }
    }, [open, initialConfig]);

    const handleLinkChange = (key, value) => {
        setLinks(prev => ({...prev, [key]: value}));

        // Автоматически добавляем в order, если ссылка заполнена и её там нет
        if (value.trim() && !order.includes(key)) {
            setOrder(prev => [...prev, key]);
        }
        // Удаляем из order, если ссылка очищена
        if (!value.trim() && order.includes(key)) {
            setOrder(prev => prev.filter(k => k !== key));
        }
    };

    const moveUp = (index) => {
        if (index === 0) return;
        const newOrder = [...order];
        [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
        setOrder(newOrder);
    };

    const moveDown = (index) => {
        if (index === order.length - 1) return;
        const newOrder = [...order];
        [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
        setOrder(newOrder);
    };

    const handleSave = async () => {
        // Фильтруем order, оставляя только те типы, для которых есть ссылка
        const validOrder = order.filter(key => links[key]?.trim());

        setIsSaving(true);
        try {
            await onSave({
                links: links,
                order: validOrder,
            });
            onClose();
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        if (isSaving) return;
        // Сбрасываем к начальным значениям
        setLinks(initialConfig?.links || {});
        setOrder(initialConfig?.order || []);
        onClose();
    };

    // Разделяем типы на заполненные и пустые
    const filledTypes = LINK_TYPES.filter(type => links[type.key]?.trim());
    const emptyTypes = LINK_TYPES.filter(type => !links[type.key]?.trim());

    // Сортируем заполненные типы по order
    const sortedFilledTypes = [...filledTypes].sort((a, b) => {
        const indexA = order.indexOf(a.key);
        const indexB = order.indexOf(b.key);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
    });

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth disableEscapeKeyDown={isSaving}>
            <DialogTitle>Настройка ссылок на отзывы</DialogTitle>

            <DialogContent>
                <Typography variant="subtitle2" component={'pre'}
                            sx={{mb: 2, color: 'text.secondary', whiteSpace: 'pre-wrap'}}>{`Ссылки отправляются пациентам по очереди при оставлении отзыва.
Первый отзыв → “Ссылка на отзыв №1”, второй → “Ссылка на отзыв №2” и т.д.

Если все отзывы на клинику оставлены или поля пустые — предлагается оставить отзыв на врача (если ссылка заполнена в карточке врача)`}
                </Typography>
                <Box sx={{pt: 1}}>
                    {/* Заполненные ссылки с возможностью изменения порядка */}
                    {sortedFilledTypes.length > 0 && (
                        <>
                            <Typography variant="subtitle2" sx={{mb: 2, color: 'text.secondary'}}>
                                Активные ссылки (можно менять порядок)
                            </Typography>
                            {sortedFilledTypes.map((type, index) => (
                                <Box
                                    key={type.key}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mb: 2,
                                        p: 1.5,
                                        bgcolor: 'action.hover',
                                        borderRadius: 1,
                                    }}
                                >
                                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                                        <IconButton
                                            size="small"
                                            onClick={() => moveUp(order.indexOf(type.key))}
                                            disabled={order.indexOf(type.key) === 0 || isSaving}
                                        >
                                            <ArrowUpward fontSize="small"/>
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => moveDown(order.indexOf(type.key))}
                                            disabled={order.indexOf(type.key) === order.length - 1 || isSaving}
                                        >
                                            <ArrowDownward fontSize="small"/>
                                        </IconButton>
                                    </Box>
                                    <TextField
                                        label={type.label}
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        value={links[type.key] || ''}
                                        onChange={(e) => handleLinkChange(type.key, e.target.value)}
                                        placeholder="https://"
                                        disabled={isSaving}
                                    />
                                </Box>
                            ))}
                        </>
                    )}

                    {/* Разделитель, если есть и заполненные и пустые */}
                    {sortedFilledTypes.length > 0 && emptyTypes.length > 0 && (
                        <Divider sx={{my: 3}}/>
                    )}

                    {/* Пустые ссылки (без кнопок сортировки) */}
                    {emptyTypes.length > 0 && (
                        <>
                            {emptyTypes.map((type) => (
                                <Box key={type.key} sx={{mb: 2}}>
                                    <TextField
                                        label={type.label}
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        value={links[type.key] || ''}
                                        onChange={(e) => handleLinkChange(type.key, e.target.value)}
                                        placeholder="https://"
                                        disabled={isSaving}
                                    />
                                </Box>
                            ))}
                        </>
                    )}

                    {sortedFilledTypes.length === 0 && emptyTypes.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            Нет доступных типов ссылок
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel} disabled={isSaving}>
                    Отмена
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={isSaving}>
                    {isSaving ? 'Сохранение...' : 'Сохранить'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReviewLinksModal;
